// @ts-nocheck
import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Plus, Minus, Box } from 'lucide-react';
import { Material, AdditionalOption, CalculatorItem, ProductParams } from '@/types/Offer';
import { productTypes, expositorTypes } from '@/constants/materials';
import { materials } from '@/constants/materials';
import { additionalOptions } from '@/constants/options';
import { EXPOSITOR_TYPES } from '@/constants/expositorTypes';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CalculatorCard } from '@/components/ui/CalculatorCard';
import { ExpositorSection } from './ExpositorSection';

// Lazy load 3D viewer to improve initial load time
const Product3DViewer = lazy(() => 
  import('./components/Product3DViewer').then(module => ({ 
    default: module.Product3DViewer 
  }))
);

interface CalculatorProps {
  onAddToOffer: (item: CalculatorItem) => void;
  viewMode?: 'salesperson' | 'client';
}

export const Calculator: React.FC<CalculatorProps> = ({ onAddToOffer, viewMode = 'salesperson' }) => {
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [selectedExpositorType, setSelectedExpositorType] = useState<string>('');
  const [selectedMaterial, setSelectedMaterial] = useState<string>('');
  const [thickness, setThickness] = useState<number>(3);
  const [dimensions, setDimensions] = useState({
    width: 300,
    height: 200,
    depth: 150,
  });
  const [quantity, setQuantity] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, boolean>>({});
  const [optionQuantities, setOptionQuantities] = useState<Record<string, number>>({});
  const [presetName, setPresetName] = useState<string>('');

  // Sprawdź czy są dane z Marketplace
  useEffect(() => {
    const presetData = localStorage.getItem('calculatorPreset');
    if (presetData) {
      try {
        const data = JSON.parse(presetData);
        
        // Mapowanie typu kalkulatora na typ produktu
        const productTypeMap: Record<string, string> = {
          'standard': 'formatka',
          'display': 'ekspozytor',
          'organizer': 'pojemnik',
          'shield': 'obudowa',
          'signage': 'kaseton'
        };
        
        const mappedProduct = productTypeMap[data.productType] || data.productType;
        setSelectedProduct(mappedProduct);
        setPresetName(data.name || '');
        
        if (data.dimensions) {
          setDimensions({
            width: data.dimensions.width || 300,
            height: data.dimensions.height || 200,
            depth: data.dimensions.depth || 150
          });
        }
        
        // Ustaw domyślny materiał na podstawie typu produktu
        if (data.materials && data.materials.length > 0) {
          const materialMap: Record<string, string> = {
            'PMMA 3mm': 'plexi',
            'PMMA 5mm': 'plexi',
            'PMMA 3mm satynowa': 'plexi_satyna',
            'PMMA 5mm crystal clear': 'plexi',
            'PMMA 5mm opal': 'plexi_opal',
            'PMMA 8mm crystal clear': 'plexi',
            'PMMA 4mm': 'plexi',
            'PETG 3mm': 'petg',
            'PETG 4mm': 'petg',
            'Poliwęglan 5mm': 'poliweg',
            'Poliwęglan 8mm': 'poliweg',
            'Dibond 3mm': 'dibond',
            'Dibond szczotkowany 3mm': 'dibond'
          };
          
          const material = data.materials[0];
          const mappedMaterial = materialMap[material] || 'plexi';
          setSelectedMaterial(mappedMaterial);
          
          // Ustaw grubość na podstawie materiału
          const thicknessMatch = material.match(/\d+mm/);
          if (thicknessMatch) {
            const thicknessValue = parseInt(thicknessMatch[0]);
            setThickness(thicknessValue);
          }
        }
        
        // Ustaw złożoność jako opcję
        if (data.complexity) {
          const complexityMap: Record<string, number> = {
            'Proste': 1,
            'Średnie': 2,
            'Złożone': 3
          };
          // Tu możesz dodać logikę dla złożoności jeśli potrzebujesz
        }
        
        // Wyczyść dane z localStorage po użyciu
        localStorage.removeItem('calculatorPreset');
        
        // Pokaż powiadomienie
        const notification = document.createElement('div');
        notification.className = 'fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-xl z-50 animate-slide-in';
        notification.textContent = `Załadowano dane z Marketplace: ${data.name}`;
        document.body.appendChild(notification);
        
        setTimeout(() => {
          notification.remove();
        }, 3000);
        
      } catch (error) {
        console.error('Błąd ładowania danych z Marketplace:', error);
      }
    }
  }, []);

  const [productParams, setProductParams] = useState<ProductParams>({
    shelves: 3,
    partitions: 0,
    ledLength: 100,
    hookRows: 3,
    hookCols: 4,
    pockets: 3,
    kasetonType: 'plexi' as 'plexi' | 'dibond',
    bottomMaterial: '',
    bottomThickness: 3,
    graphicsSizeMode: 'percentage',
    graphicsPercentage: 100,
    graphicsWidth: 200,
    graphicsHeight: 200,
    // Nowe parametry dla gabloty
    gabinetBackMaterial: '',
    gabinetFrontOnly: false,
    // Nowe parametry dla obudowy
    includeTop: false,
    ventilationPercent: 0,
    serviceHatch: false,
    cableGlands: 0,
  });

  const [show3D, setShow3D] = useState(false);
  const [calculations, setCalculations] = useState({
    surface: 0,
    weight: 0,
    materialCost: 0,
    optionsCost: 0,
    unitPrice: 0,
    totalPrice: 0,
    piecesPerPallet: 0,
    piecesPerBox: 0,
    totalWeight: 0,
    boxDimensions: { width: 0, height: 0, depth: 0 },
    boxSurface: 0,
    boxWeight: 0,
    piecesPerBoxOptimal: 0,
    boxesTotal: 0,
    palletsTotal: 0,
    palletLayers: 0,
    boxesPerLayer: 0,
    unitWeight: 0,
    piecesPerFefco: 0,
    directPalletArrangement: {
      itemsPerLayer: 0,
      layers: 0,
      itemsWidth: 0,
      itemsLength: 0,
      totalItems: 0
    },
    costBreakdown: {
      materialCost: 0,
      wasteCost: 0,
      laborCost: 0,
      optionsCost: 0,
      margin: 0,
    },
  });

  // Funkcja obliczająca powierzchnię
  const calculateSurface = (): number => {
    const w = dimensions.width / 1000;
    const h = dimensions.height / 1000;
    const d = dimensions.depth / 1000;

    switch (selectedProduct) {
      case 'formatka':
        return w * h;

      case 'pojemnik':
        const frontBack = 2 * (w * h);
        const sides = 2 * (d * h);
        let bottomArea = w * d;

        if (selectedOptions.dno_inny_material && productParams.bottomMaterial) {
          bottomArea = 0;
        }

        const lid = selectedOptions.wieko ? w * d : 0;
        const partitionArea = productParams.partitions * h * d;
        return frontBack + sides + bottomArea + lid + partitionArea;

      case 'gablota':
        // Sprawdź czy używamy różnych materiałów
        if (productParams.gabinetFrontOnly) {
          // Tylko front z wybranego materiału (plexi)
          return w * h;
        } else {
          // Cała gablota z jednego materiału (jak wcześniej)
          return 2 * (w * h) + 2 * (d * h) + w * d + (selectedOptions.wieko ? w * d : 0);
        }

      case 'obudowa':
        let obudowaSurface = 2 * (w * h) + 2 * (d * h) + w * d;
        
        // Dodaj górę jeśli wybrana
        if (productParams.includeTop) {
          obudowaSurface += w * d;
        }
        
        // Odejmij powierzchnię otworów wentylacyjnych
        if (productParams.ventilationPercent > 0) {
          obudowaSurface *= (1 - productParams.ventilationPercent / 100);
        }
        
        return obudowaSurface;

      case 'ekspozytor':
        return calculateExpositorSurface(w, h, d);

      case 'kaseton':
        return w * h;

      case 'ledon':
        return w * h;

      case 'impuls':
        const back = w * h;
        const impSides = 2 * (d * h);
        const shelvesArea = productParams.shelves * w * d;
        // Poprawka: wsporniki półek to paski pod półkami o wysokości 50mm
        const shelfSupports = productParams.shelves * w * 0.05; // 50mm wysokości wspornika
        return back + impSides + shelvesArea + shelfSupports;

      default:
        return w * h;
    }
  };

  // Funkcja obliczająca powierzchnię ekspozytorów - ULEPSZONA WERSJA
  const calculateExpositorSurface = (w: number, h: number, d: number): number => {
    const expositorType = EXPOSITOR_TYPES[selectedExpositorType];
    
    // Jeśli mamy nowy typ z funkcją kalkulacji, użyj jej
    if (expositorType && expositorType.calculateSurface) {
      // Przygotuj parametry uwzględniając obecną strukturę productParams
      const params = {
        ...productParams,
        withSides: selectedOptions.boki || false,
        withTopper: selectedOptions.topper || false,
        topper: selectedOptions.topper || false
      };
      return expositorType.calculateSurface(w, h, d, params);
    }
    
    // Fallback do starej implementacji dla kompatybilności
    switch (selectedExpositorType) {
      case 'podstawkowy':
        const base = w * d;
        const back = w * h;
        const sides = 2 * (d * h);
        const topper = selectedOptions.topper ? w * (h * 0.2) : 0;
        return base + back + sides + topper;

      case 'schodkowy':
        const baseArea = w * d;
        const backArea = w * h;
        const sidesArea = 2 * (d * h);
        const stepArea = productParams.shelves * w * (d / productParams.shelves);
        const risers = productParams.shelves * w * 0.05;
        return baseArea + backArea + sidesArea + stepArea + risers;

      case 'z_haczykami':
        return w * d + w * h + 2 * (d * h) + (selectedOptions.topper ? w * (h * 0.2) : 0);

      case 'wiszacy':
        const backPanel = w * h;
        const pockets = productParams.pockets * w * 0.1;
        return backPanel + pockets;

      case 'stojak':
        const heavyBase = w * d * 1.5;
        const frame = 2 * (h * 0.1);
        const segments = productParams.shelves * w * 0.2;
        return heavyBase + frame + segments;

      case 'kosmetyczny':
        const kosBase = w * d;
        const kosBack = w * h;
        const kosSides = 2 * (d * h);
        const kosShelves = productParams.shelves * w * d;
        const kosDividers = productParams.shelves * productParams.partitions * d * 0.08;
        return kosBase + kosBack + kosSides + kosShelves + kosDividers;

      default:
        return w * h + 2 * (d * h) + w * d;
    }
  };

  // Funkcja obliczająca wagę
  const calculateWeight = (surface: number, material: Material | undefined, thickness: number): number => {
    const densityKgM3 = material?.density || 1190;
    const thicknessMm = thickness || 3;
    return surface * (thicknessMm / 1000) * densityKgM3;
  };

  // Funkcja obliczająca wymiary kartonu
  const calculateBoxDimensions = () => {
    const isFlat = ['formatka', 'kaseton', 'ledon'].includes(selectedProduct);

    if (isFlat) {
      // Dla kasetonów używamy grubości 100mm
      const actualThickness = selectedProduct === 'kaseton' ? 100 : thickness;
      return {
        width: dimensions.width + 40,
        height: dimensions.height + 40,
        depth: Math.max(actualThickness * quantity + 20, 50),
      };
    } else {
      return {
        width: dimensions.width + 40,
        height: dimensions.height + 40,
        depth: dimensions.depth + 40,
      };
    }
  };

  // Funkcja obliczająca ilość w kartonie zbiorczym
  const calculatePiecesPerBox = (productDims: any, _boxDims: any, productThickness?: number): number => {
    const maxBoxDims = { width: 600, height: 400, depth: 400 };

    if (['formatka', 'kaseton', 'ledon'].includes(selectedProduct)) {
      const actualThickness = productThickness || thickness || 3;
      const stackHeight = Math.floor((maxBoxDims.depth - 20) / actualThickness);
      return Math.max(1, Math.min(stackHeight, 50));
    }

    const orientations = [
      { w: productDims.width, h: productDims.height, d: productDims.depth },
      { w: productDims.width, h: productDims.depth, d: productDims.height },
      { w: productDims.height, h: productDims.width, d: productDims.depth },
      { w: productDims.height, h: productDims.depth, d: productDims.width },
      { w: productDims.depth, h: productDims.width, d: productDims.height },
      { w: productDims.depth, h: productDims.height, d: productDims.width },
    ];

    let maxPieces = 1;

    orientations.forEach((orient) => {
      const piecesX = Math.floor(maxBoxDims.width / (orient.w + 10));
      const piecesY = Math.floor(maxBoxDims.height / (orient.h + 10));
      const piecesZ = Math.floor(maxBoxDims.depth / (orient.d + 10));
      const total = piecesX * piecesY * piecesZ;
      if (total > maxPieces) maxPieces = total;
    });

    return Math.max(1, maxPieces);
  };

  // Funkcja obliczająca układ na palecie
  const calculatePalletArrangement = (boxDims: any) => {
    const palletDims = { width: 1200, height: 800 };
    const maxHeight = 1656;

    const arrangements = [
      { w: boxDims.width, d: boxDims.depth },
      { w: boxDims.depth, d: boxDims.width },
    ];

    let bestArrangement = { boxesPerLayer: 0, layers: 0 };

    arrangements.forEach((arr) => {
      const boxesX = Math.floor(palletDims.width / arr.w);
      const boxesY = Math.floor(palletDims.height / arr.d);
      const boxesPerLayer = boxesX * boxesY;
      const layers = Math.floor(maxHeight / boxDims.height);

      if (boxesPerLayer * layers > bestArrangement.boxesPerLayer * bestArrangement.layers) {
        bestArrangement = {
          boxesPerLayer,
          layers,
        };
      }
    });

    return bestArrangement;
  };

  // Funkcja obliczająca ilość w kartonie FEFCO
  const calculatePiecesPerFefco = (productDims: any, productThickness?: number): number => {
    const fefcoDims = { width: 300, height: 400, depth: 400 };

    if (['formatka', 'kaseton', 'ledon'].includes(selectedProduct)) {
      const actualThickness = productThickness || thickness || 3;
      const stackHeight = Math.floor((fefcoDims.depth - 20) / actualThickness);
      return Math.max(1, Math.min(stackHeight, 30));
    }

    const orientations = [
      { w: productDims.width, h: productDims.height, d: productDims.depth },
      { w: productDims.width, h: productDims.depth, d: productDims.height },
      { w: productDims.height, h: productDims.width, d: productDims.depth },
      { w: productDims.height, h: productDims.depth, d: productDims.width },
      { w: productDims.depth, h: productDims.width, d: productDims.height },
      { w: productDims.depth, h: productDims.height, d: productDims.width },
    ];

    let maxPieces = 0;

    orientations.forEach((orient) => {
      const piecesX = Math.floor(fefcoDims.width / (orient.w + 5));
      const piecesY = Math.floor(fefcoDims.height / (orient.h + 5));
      const piecesZ = Math.floor(fefcoDims.depth / (orient.d + 5));
      const total = piecesX * piecesY * piecesZ;
      if (total > maxPieces) maxPieces = total;
    });

    return Math.max(1, maxPieces);
  };

  // Funkcja do obliczania układu produktów bezpośrednio na palecie
  const calculateDirectPalletArrangement = (productDims: any, productThickness?: number) => {
    const palletDims = { width: 800, length: 1200 };
    const maxHeight = 1600;

    // Sprawdź czy produkt jest płaski
    const isFlat = ['formatka', 'kaseton', 'ledon'].includes(selectedProduct);

    if (isFlat) {
      // Dla płaskich produktów - układamy płasko jeden na drugim
      const actualThickness = productThickness || thickness || 3; // grubość w mm
      
      // Sprawdź różne orientacje układania na palecie
      const orientations = [
        { w: productDims.width, h: productDims.height },
        { w: productDims.height, h: productDims.width }
      ];
      
      let bestArrangement = {
        itemsPerLayer: 0,
        layers: 0,
        itemsWidth: 0,
        itemsLength: 0,
        totalItems: 0
      };
      
      orientations.forEach((orient) => {
        // Ile sztuk mieści się na szerokość i długość palety
        const itemsWidth = Math.floor(palletDims.width / orient.w);
        const itemsLength = Math.floor(palletDims.length / orient.h);
        const itemsPerLayer = itemsWidth * itemsLength;
        
        // Ile warstw zmieści się na wysokość
        const layers = Math.floor(maxHeight / actualThickness);
        const totalItems = itemsPerLayer * layers;
        
        if (totalItems > bestArrangement.totalItems) {
          bestArrangement = {
            itemsPerLayer,
            layers,
            itemsWidth,
            itemsLength,
            totalItems
          };
        }
      });
      
      return bestArrangement;
    } else {
      // Dla produktów 3D - układamy obok siebie
      const orientations = [
        { w: productDims.width, d: productDims.depth, h: productDims.height },
        { w: productDims.depth, d: productDims.width, h: productDims.height },
      ];

      let bestArrangement = { 
        itemsPerLayer: 0, 
        layers: 0,
        itemsWidth: 0,
        itemsLength: 0,
        totalItems: 0
      };

      orientations.forEach((orient) => {
        const itemsWidth = Math.floor(palletDims.width / orient.w);
        const itemsLength = Math.floor(palletDims.length / orient.d);
        const itemsPerLayer = itemsWidth * itemsLength;
        const layers = Math.floor(maxHeight / orient.h);
        const totalItems = itemsPerLayer * layers;

        if (totalItems > bestArrangement.totalItems) {
          bestArrangement = {
            itemsPerLayer,
            layers,
            itemsWidth,
            itemsLength,
            totalItems
          };
        }
      });

      return bestArrangement;
    }
  };

  // Funkcja obliczająca koszt opcji
  const calculateOptionsCost = (): number => {
    let cost = 0;
    const surface = calculateSurface();
    const w = dimensions.width / 1000;
    const h = dimensions.height / 1000;
    const d = dimensions.depth / 1000;

    Object.entries(selectedOptions).forEach(([optionId, isSelected]) => {
      if (isSelected && optionId !== 'wieko') {
        const option = additionalOptions.find((o) => o.id === optionId);
        if (option) {
          const qty = optionQuantities[optionId] || 1;

          if (option.unit === 'm²') {
            // Specjalna obsługa dla grafiki
            if (optionId === 'grafika' || optionId === 'grafika_dwustronna') {
              let graphicsSurface = surface;
              
              if (productParams.graphicsSizeMode === 'percentage') {
                graphicsSurface = surface * (productParams.graphicsPercentage / 100);
              } else if (productParams.graphicsSizeMode === 'custom') {
                graphicsSurface = (productParams.graphicsWidth / 1000) * (productParams.graphicsHeight / 1000);
              }
              
              cost += option.price * graphicsSurface;
            } else {
              cost += option.price * surface;
            }
          } else if (option.unit === 'mb') {
            if (optionId === 'polerowanie') {
              let edgeLength = 0;

              if (selectedProduct === 'formatka') {
                edgeLength = 2 * (w + h);
              } else if (['pojemnik', 'gablota'].includes(selectedProduct)) {
                edgeLength = 2 * (2 * w + 2 * h) + 2 * (2 * d + 2 * h) + 2 * w + 2 * d;
                if (selectedOptions.wieko) {
                  edgeLength += 2 * w + 2 * d;
                }
              } else if (selectedProduct === 'ekspozytor') {
                edgeLength = calculateExpositorEdges();
              } else if (selectedProduct === 'impuls') {
                edgeLength = 2 * (w + h) + 4 * d + productParams.shelves * 2 * w;
              }

              cost += option.price * edgeLength;
            } else if (optionId.includes('led')) {
              const ledLengthM = productParams.ledLength / 100;
              cost += option.price * ledLengthM;
            } else if (optionId === 'tasma') {
              // Taśma liczona za metr bieżący
              const perimeter = 2 * (dimensions.width / 1000 + dimensions.height / 1000);
              cost += option.price * perimeter * qty;
            } else if (optionId === 'karton') {
              // Karton - obliczamy zużycie tektury
              const boxDims = calculateBoxDimensions();
              const cardboardArea = 2 * ((boxDims.width * boxDims.height) + (boxDims.width * boxDims.depth) + (boxDims.height * boxDims.depth)) / 1000000;
              cost += option.price * cardboardArea * qty; // 8 zł/m²
            }
          } else if (option.unit === 'szt' || option.unit === 'komplet') {
            if (optionId === 'karton') {
              // Karton - obliczamy zużycie tektury
              const boxDims = calculateBoxDimensions();
              const cardboardArea = 2 * ((boxDims.width * boxDims.height) + (boxDims.width * boxDims.depth) + (boxDims.height * boxDims.depth)) / 1000000;
              cost += option.price * cardboardArea * qty; // 8 zł/m²
            } else {
              cost += option.price * qty;
            }
          }
        }
      }
    });

    if (selectedOptions.led_standard || selectedOptions.led_cob || selectedOptions.led_rgb) {
      if (!selectedOptions.zasilacz_led) {
        cost += 80;
      }
    }

    // Dodatkowe koszty dla obudowy
    if (selectedProduct === 'obudowa') {
      if (productParams.serviceHatch) {
        cost += 45; // Koszt klapki serwisowej
      }
      if (productParams.cableGlands > 0) {
        cost += productParams.cableGlands * 15; // Koszt przepustów kablowych
      }
    }

    return cost;
  };

  // Funkcja pomocnicza do obliczania krawędzi ekspozytorów
  const calculateExpositorEdges = (): number => {
    const w = dimensions.width / 1000;
    const h = dimensions.height / 1000;
    const d = dimensions.depth / 1000;

    switch (selectedExpositorType) {
      case 'podstawkowy':
        return 2 * w + 2 * d + 2 * h + w;
      case 'schodkowy':
        return 2 * w + 2 * d + 2 * h + productParams.shelves * w;
      case 'z_haczykami':
        return 2 * w + 2 * d + 2 * h + w;
      case 'wiszacy':
        return 2 * (w + h);
      case 'stojak':
        return 2 * (w + d) + 4 * h;
      case 'kosmetyczny':
        return 2 * w + 2 * d + 2 * h + productParams.shelves * 2 * w;
      default:
        return 2 * (w + h);
    }
  };

  // Komponent wizualizacji palety
  const PalletVisualization: React.FC<{
    arrangement: {
      itemsWidth: number;
      itemsLength: number;
      layers: number;
      itemsPerLayer: number;
      totalItems: number;
    }
  }> = ({ arrangement }) => {
    const isFlat = ['formatka', 'kaseton', 'ledon'].includes(selectedProduct);
    
    return (
      <div className="mt-4 p-4 bg-zinc-800 rounded-xl">
        <h4 className="text-sm font-medium text-white/60 mb-3">Schemat rozmieszczenia na palecie EUR (800×1200×1600 mm)</h4>
        
        {isFlat ? (
          // Wizualizacja dla płaskich produktów
          <>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center p-3 bg-zinc-700 rounded-lg">
                <div className="text-white/60 text-xs mb-1">Szerokość</div>
                <div className="text-white font-bold">{arrangement.itemsWidth} szt.</div>
              </div>
              <div className="text-center p-3 bg-zinc-700 rounded-lg">
                <div className="text-white/60 text-xs mb-1">Długość</div>
                <div className="text-white font-bold">{arrangement.itemsLength} szt.</div>
              </div>
              <div className="text-center p-3 bg-zinc-700 rounded-lg">
                <div className="text-white/60 text-xs mb-1">Wysokość</div>
                <div className="text-white font-bold">{arrangement.layers} warstw</div>
              </div>
            </div>
            
            <div className="relative h-40 bg-zinc-900 rounded-lg overflow-hidden mb-4">
              <div className="absolute inset-0 flex items-center justify-center">
                {arrangement.itemsPerLayer > 1 ? (
                  // Pokaż układ z góry gdy więcej niż 1 sztuka na warstwę
                  <div className="grid gap-1" style={{
                    gridTemplateColumns: `repeat(${arrangement.itemsLength}, 1fr)`,
                    gridTemplateRows: `repeat(${arrangement.itemsWidth}, 1fr)`,
                    width: '90%',
                    height: '90%'
                  }}>
                    {Array.from({ length: arrangement.itemsPerLayer }).map((_, i) => (
                      <div key={i} className="bg-orange-500/30 border border-orange-500/50 rounded-sm" />
                    ))}
                  </div>
                ) : (
                  // Pokaż stos dla pojedynczych dużych formatek
                  <div className="relative" style={{ width: '60%', height: '80%' }}>
                    {Array.from({ length: Math.min(10, arrangement.layers) }).map((_, i) => (
                      <div
                        key={i}
                        className="absolute bg-orange-500/40 border border-orange-500 rounded-sm"
                        style={{
                          width: '100%',
                          height: '8px',
                          bottom: `${i * 10}px`,
                          left: `${i * 2}px`
                        }}
                      />
                    ))}
                    {arrangement.layers > 10 && (
                      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 text-orange-400 text-sm">
                        +{arrangement.layers - 10} więcej...
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="absolute bottom-2 left-2 text-xs text-white/60">
                {arrangement.itemsPerLayer > 1 ? 'Widok z góry - 1 warstwa' : 'Widok z boku - stos'}
              </div>
            </div>
            
            <div className="text-center p-3 bg-zinc-700 rounded-lg">
              <div className="text-2xl font-bold text-orange-500">
                {arrangement.itemsWidth} × {arrangement.itemsLength} × {arrangement.layers}
              </div>
              <div className="text-sm text-white/60 mt-1">
                = {arrangement.totalItems} szt. na palecie
              </div>
              <div className="text-xs text-white/40 mt-2">
                Grubość produktu: {selectedProduct === 'kaseton' ? '100' : thickness} mm
              </div>
            </div>
          </>
        ) : (
          // Wizualizacja dla produktów 3D
          <>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center p-3 bg-zinc-700 rounded-lg">
                <div className="text-white/60 text-xs mb-1">Szerokość</div>
                <div className="text-white font-bold">{arrangement.itemsWidth} szt.</div>
              </div>
              <div className="text-center p-3 bg-zinc-700 rounded-lg">
                <div className="text-white/60 text-xs mb-1">Długość</div>
                <div className="text-white font-bold">{arrangement.itemsLength} szt.</div>
              </div>
              <div className="text-center p-3 bg-zinc-700 rounded-lg">
                <div className="text-white/60 text-xs mb-1">Wysokość</div>
                <div className="text-white font-bold">{arrangement.layers} warstw</div>
              </div>
            </div>
            
            <div className="relative h-40 bg-zinc-900 rounded-lg overflow-hidden mb-4">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="grid gap-1" style={{
                  gridTemplateColumns: `repeat(${arrangement.itemsLength}, 1fr)`,
                  gridTemplateRows: `repeat(${arrangement.itemsWidth}, 1fr)`,
                  width: '90%',
                  height: '90%'
                }}>
                  {Array.from({ length: arrangement.itemsPerLayer }).map((_, i) => (
                    <div key={i} className="bg-orange-500/30 border border-orange-500/50 rounded-sm" />
                  ))}
                </div>
              </div>
              <div className="absolute bottom-2 left-2 text-xs text-white/60">Widok z góry - 1 warstwa</div>
            </div>
            
            <div className="text-center p-3 bg-zinc-700 rounded-lg">
              <div className="text-2xl font-bold text-orange-500">
                {arrangement.itemsWidth} × {arrangement.itemsLength} × {arrangement.layers}
              </div>
              <div className="text-sm text-white/60 mt-1">
                = {arrangement.totalItems} szt. na palecie
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  // Główna funkcja kalkulacyjna
  useEffect(() => {
    if (selectedProduct && (selectedProduct !== 'ekspozytor' || selectedExpositorType)) {
      if (selectedProduct === 'kaseton' && productParams.kasetonType) {
        const surface = calculateSurface();
        const basePrice = productParams.kasetonType === 'plexi' ? 1550 : 1400;
        let optionsCost = calculateOptionsCost();
        // ZMIANA: Cena to basePrice * powierzchnia (już jest cena gotowego kasetonu)
        const unitPrice = basePrice * surface + optionsCost;
        // Kasetony mają standardową grubość 100mm (głębokość konstrukcji)
        const kasetonThickness = 100;
        // Waga kasetonu to nie tylko front, ale cała konstrukcja
        // Zakładam że kaseton waży ok. 15-20 kg/m² frontu
        const weight = productParams.kasetonType === 'plexi' ? surface * 18 : surface * 15;

        const boxDims = calculateBoxDimensions();
        const boxSurface = (2 * (boxDims.width * boxDims.height + boxDims.width * boxDims.depth + boxDims.height * boxDims.depth)) / 1000000;
        const boxWeight = boxSurface * 0.6;
        const piecesPerBox = calculatePiecesPerBox(dimensions, boxDims, kasetonThickness);
        const boxesTotal = Math.ceil(quantity / piecesPerBox);
        const palletInfo = calculatePalletArrangement(boxDims);
        const piecesPerFefco = calculatePiecesPerFefco(dimensions, kasetonThickness);
        // Przekazujemy grubość kasetonu do funkcji układania na palecie
        const directPalletArrangement = calculateDirectPalletArrangement(dimensions, kasetonThickness);
        const palletsTotal = Math.ceil(boxesTotal / (palletInfo.boxesPerLayer * palletInfo.layers));

        // ZMIANA: Nie dzielimy ceny na materiał/robociznę bo to już jest cena końcowa
        const materialCost = basePrice * surface; // Pełna cena końcowa
        const wasteCost = 0; // Brak dodatkowego odpadu - już wliczony w cenę
        const laborCost = 0; // Robocizna już wliczona w cenę
        const margin = 0; // Marża już wliczona w cenę

        setCalculations({
          surface: surface,
          weight: weight,
          materialCost: basePrice * surface,
          optionsCost: optionsCost,
          unitPrice: unitPrice,
          totalPrice: unitPrice * quantity,
          piecesPerPallet: directPalletArrangement.totalItems,
          piecesPerBox: piecesPerBox,
          totalWeight: weight * quantity,
          boxDimensions: boxDims,
          boxSurface: boxSurface,
          boxWeight: boxWeight,
          piecesPerBoxOptimal: piecesPerBox,
          boxesTotal: boxesTotal,
          palletsTotal: palletsTotal,
          palletLayers: palletInfo.layers,
          boxesPerLayer: palletInfo.boxesPerLayer,
          unitWeight: weight,
          piecesPerFefco: piecesPerFefco,
          directPalletArrangement: directPalletArrangement,
          costBreakdown: {
            materialCost,
            wasteCost,
            laborCost,
            optionsCost,
            margin,
          },
        });

        return;
      }

      const product = productTypes.find((p) => p.id === selectedProduct);
      const material = materials.find((m) => m.id === selectedMaterial);

      if (product && material) {
        const surface = calculateSurface();
        const surfaceWithWaste = surface * (1 + product.waste);

        let materialCost;
        
        // Specjalna obsługa dla plexi kolorowej z cenami per grubość
        if (selectedMaterial === 'plexi_color' && material.pricePerThickness) {
          const pricePerM2 = material.pricePerThickness[thickness] || material.basePrice * thickness;
          materialCost = pricePerM2 * surfaceWithWaste;
        } else {
          materialCost = material.basePrice * thickness * surfaceWithWaste;
        }

        if (selectedOptions.klejenie_uv) {
          materialCost *= 1.15; // Zwiększone z 10% na 15%
        }

        // Specjalna obsługa dla gabloty z różnymi materiałami
        if (selectedProduct === 'gablota' && productParams.gabinetFrontOnly && productParams.gabinetBackMaterial) {
          // Oblicz koszt frontu (z wybranego materiału)
          const frontArea = (dimensions.width / 1000) * (dimensions.height / 1000);
          const frontAreaWithWaste = frontArea * (1 + product.waste);
          
          // Koszt frontu
          let frontCost;
          if (selectedMaterial === 'plexi_color' && material.pricePerThickness) {
            const pricePerM2 = material.pricePerThickness[thickness] || material.basePrice * thickness;
            frontCost = pricePerM2 * frontAreaWithWaste;
          } else {
            frontCost = material.basePrice * thickness * frontAreaWithWaste;
          }
          
          // Oblicz koszt tyłu i boków (z tańszego materiału)
          const backAndSidesArea = ((dimensions.width / 1000) * (dimensions.height / 1000)) + 
                                   2 * ((dimensions.depth / 1000) * (dimensions.height / 1000)) + 
                                   ((dimensions.width / 1000) * (dimensions.depth / 1000));
          const backAndSidesAreaWithWaste = backAndSidesArea * (1 + product.waste);
          
          // Ceny materiałów alternatywnych
          const backMaterialPrices: Record<string, number> = {
            dibond: 45,
            sklejka: 30
          };
          
          const backCost = backMaterialPrices[productParams.gabinetBackMaterial] * backAndSidesAreaWithWaste;
          
          // Całkowity koszt materiału to suma frontu i tyłu/boków
          materialCost = frontCost + backCost;
        }

        if (selectedProduct === 'pojemnik' && selectedOptions.dno_inny_material && productParams.bottomMaterial) {
          const bottomMaterial = materials.find((m) => m.id === productParams.bottomMaterial);
          if (bottomMaterial) {
            const bottomArea = (dimensions.width / 1000) * (dimensions.depth / 1000);
            const bottomAreaWithWaste = bottomArea * (1 + product.waste);
            let bottomCost = bottomMaterial.basePrice * productParams.bottomThickness * bottomAreaWithWaste;

            if (productParams.bottomMaterial === 'plexi_color') {
              bottomCost *= bottomMaterial.colorMultiplier || 1.4;
            }

            materialCost += bottomCost;
          }
        }

        let optionsCost = calculateOptionsCost();

        if (selectedProduct === 'ledon') {
          const ledLengthM = productParams.ledLength / 100;

          if (!selectedOptions.led_standard && !selectedOptions.led_cob && !selectedOptions.led_rgb) {
            optionsCost += 30 * ledLengthM;
          }

          if (selectedOptions.wodoodpornosc) {
            const ledCost = optionsCost;
            optionsCost += ledCost * 0.18; // Ustawione na 18%
          }
        }

        const weight = calculateWeight(surface, material, thickness);

        // Minimalne zamówienie 0.1m² dla pojedynczej sztuki
        const minSurface = quantity === 1 ? Math.max(surface, 0.1) : surface;
        const adjustedMaterialCost = quantity === 1 && surface < 0.1 ? materialCost * (0.1 / surface) : materialCost;

        const baseCost = adjustedMaterialCost;
        const wasteCost = baseCost * product.waste;
        const laborCost = baseCost * 0.333; // 33.3% robocizna
        const totalCostBeforeMargin = baseCost + laborCost;
        const unitPrice = totalCostBeforeMargin * product.multiplier + optionsCost;
        const margin = unitPrice - baseCost - wasteCost - laborCost - optionsCost;

        const boxDims = calculateBoxDimensions();
        const boxSurface = (2 * (boxDims.width * boxDims.height + boxDims.width * boxDims.depth + boxDims.height * boxDims.depth)) / 1000000;
        const boxWeight = boxSurface * 0.6;
        const piecesPerBox = calculatePiecesPerBox(dimensions, boxDims);
        const boxesTotal = Math.ceil(quantity / piecesPerBox);
        const palletInfo = calculatePalletArrangement(boxDims);
        const piecesPerFefco = calculatePiecesPerFefco(dimensions, thickness);
        const directPalletArrangement = calculateDirectPalletArrangement(dimensions, thickness);
        const palletsTotal = Math.ceil(boxesTotal / (palletInfo.boxesPerLayer * palletInfo.layers));

        setCalculations({
          surface: surface,
          weight: weight,
          materialCost: materialCost,
          optionsCost: optionsCost,
          unitPrice: unitPrice,
          totalPrice: unitPrice * quantity,
          piecesPerPallet: directPalletArrangement.totalItems,
          piecesPerBox: piecesPerBox,
          totalWeight: weight * quantity + boxWeight * boxesTotal,
          boxDimensions: boxDims,
          boxSurface: boxSurface,
          boxWeight: boxWeight,
          piecesPerBoxOptimal: piecesPerBox,
          boxesTotal: boxesTotal,
          palletsTotal: palletsTotal,
          palletLayers: palletInfo.layers,
          boxesPerLayer: palletInfo.boxesPerLayer,
          unitWeight: weight,
          piecesPerFefco: piecesPerFefco,
          directPalletArrangement: directPalletArrangement,
          costBreakdown: {
            materialCost: baseCost,
            wasteCost,
            laborCost,
            optionsCost,
            margin,
          },
        });
      }
    }
  }, [
    selectedProduct,
    selectedExpositorType,
    selectedMaterial,
    thickness,
    dimensions,
    quantity,
    selectedOptions,
    optionQuantities,
    productParams,
  ]);

  // Funkcje pomocnicze
  const adjustDimension = (dim: 'width' | 'height' | 'depth', delta: number) => {
    setDimensions((prev) => ({
      ...prev,
      [dim]: Math.max(10, Math.min(2000, prev[dim] + delta)),
    }));
  };

  const adjustQuantity = (delta: number) => {
    setQuantity(Math.max(1, quantity + delta));
  };

  const adjustOptionQuantity = (optionId: string, delta: number) => {
    setOptionQuantities((prev) => ({
      ...prev,
      [optionId]: Math.max(1, (prev[optionId] || 1) + delta),
    }));
  };

  const adjustProductParam = (param: string, delta: number) => {
    setProductParams((prev) => ({
      ...prev,
      [param]: Math.max(0, (prev as any)[param] + delta),
    }));
  };

  // Filtrowanie opcji w zależności od produktu
  const getAvailableOptions = (): AdditionalOption[] => {
    let options = [...additionalOptions];

    if (selectedProduct === 'kaseton') {
      return options.filter((o) =>
        ['litery_podklejane', 'litery_zlicowane', 'litery_wystajace', 'litery_halo', 'led_standard', 'led_cob', 'led_rgb', 'zasilacz_led'].includes(o.id)
      );
    }

    if (selectedProduct === 'ledon') {
      return options.filter((o) =>
        ['led_standard', 'led_cob', 'led_rgb', 'projekt_led', 'wodoodpornosc', 'zasilacz_led'].includes(o.id)
      );
    }

    if (selectedProduct === 'ekspozytor') {
      const baseOptions = ['grafika', 'grafika_dwustronna', 'polerowanie', 'karton'];
      if (['podstawkowy', 'z_haczykami', 'kosmetyczny'].includes(selectedExpositorType)) {
        baseOptions.push('topper', 'led_standard', 'zasilacz_led');
      }
      return options.filter((o) => baseOptions.includes(o.id));
    }

    if (['pojemnik', 'gablota'].includes(selectedProduct)) {
      const baseOptions = ['grafika', 'polerowanie', 'zawiasy', 'zamek', 'karton', 'klejenie_uv', 'nozki'];
      if (selectedProduct === 'pojemnik') {
        baseOptions.push('dno_inny_material');
      }
      return options.filter((o) => baseOptions.includes(o.id));
    }

    if (selectedProduct === 'obudowa') {
      return options.filter((o) =>
        ['grafika', 'polerowanie', 'karton', 'klejenie_uv', 'tasma'].includes(o.id)
      );
    }

    if (selectedProduct === 'formatka') {
      return options.filter((o) => ['grafika', 'polerowanie', 'karton'].includes(o.id));
    }

    if (selectedProduct === 'impuls') {
      return options.filter((o) =>
        ['grafika', 'grafika_dwustronna', 'polerowanie'].includes(o.id)
      );
    }

    return options;
  };

  // Funkcja dodawania do oferty
  const handleAddToOffer = () => {
    // Walidacja dla kasetonów
    if (selectedProduct === 'kaseton') {
      if (!productParams.kasetonType) {
        alert('Wybierz rodzaj kasetonu (z plexi lub dibond)');
        return;
      }
      if (calculations.unitPrice === 0) {
        alert('Błąd kalkulacji ceny. Sprawdź wymiary i opcje.');
        return;
      }
    } 
    // Walidacja dla pozostałych produktów
    else if (!selectedProduct) {
      alert('Wybierz produkt');
      return;
    } else if (!selectedMaterial) {
      alert('Wybierz materiał');
      return;
    } else if (calculations.unitPrice === 0) {
      alert('Błąd kalkulacji ceny. Sprawdź wszystkie parametry.');
      return;
    }

    const item: CalculatorItem = {
      id: Date.now(),
      product: selectedProduct,
      productName: productTypes.find((p) => p.id === selectedProduct)?.name || '',
      expositorType: selectedExpositorType,
      material: selectedProduct === 'kaseton' ? productParams.kasetonType : selectedMaterial,
      materialName: selectedProduct === 'kaseton' 
        ? (productParams.kasetonType === 'plexi' ? 'Kaseton z plexi' : 'Kaseton z dibond')
        : (materials.find((m) => m.id === selectedMaterial)?.name || ''),
      thickness: selectedProduct === 'kaseton' ? 100 : thickness,
      dimensions,
      quantity,
      options: selectedOptions,
      optionQuantities,
      productParams,
      calculations,
      unitPrice: calculations.unitPrice,
      totalPrice: calculations.totalPrice,
      costBreakdown: calculations.costBreakdown,
    };

    onAddToOffer(item);

    // Reset formularza
    setSelectedProduct('');
    setSelectedExpositorType('');
    setSelectedMaterial('');
    setThickness(3);
    setDimensions({ width: 300, height: 200, depth: 150 });
    setQuantity(1);
    setSelectedOptions({});
    setOptionQuantities({});
    setProductParams({
      shelves: 3,
      partitions: 0,
      ledLength: 100,
      hookRows: 3,
      hookCols: 4,
      pockets: 3,
      kasetonType: 'plexi',
      bottomMaterial: '',
      bottomThickness: 3,
      graphicsSizeMode: 'percentage',
      graphicsPercentage: 100,
      graphicsWidth: 200,
      graphicsHeight: 200,
    });
  };

  return (
    <div className="space-y-8">
      {/* 3D Visualization Toggle Button */}
      {selectedProduct && (
        <div className="flex justify-end">
          <button
            onClick={() => setShow3D(!show3D)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              show3D 
                ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white' 
                : 'bg-zinc-800 hover:bg-zinc-700 text-white'
            }`}
          >
            <Box className="w-5 h-5" />
            {show3D ? 'Ukryj wizualizację 3D' : 'Pokaż wizualizację 3D'}
          </button>
        </div>
      )}

      {/* 3D Viewer */}
      {show3D && selectedProduct && (
        <div className="bg-zinc-900 rounded-2xl p-4 border border-zinc-800">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <span className="text-orange-500">🎯</span> Wizualizacja 3D produktu
          </h2>
          <Suspense fallback={
            <div className="h-[500px] flex items-center justify-center">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-gray-400">Ładowanie wizualizacji 3D...</p>
              </div>
            </div>
          }>
            <Product3DViewer
              productType={selectedProduct}
              expositorType={selectedExpositorType}
              dimensions={dimensions}
              material={selectedProduct === 'kaseton' ? productParams.kasetonType : selectedMaterial}
              thickness={selectedProduct === 'kaseton' ? 100 : thickness}
              options={selectedOptions}
              productParams={productParams}
            />
          </Suspense>
        </div>
      )}
      {/* Informacja o załadowanym projekcie */}
      {presetName && (
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-4 mb-6">
          <p className="text-white font-medium">🎯 Załadowano projekt z Marketplace: <span className="font-bold">{presetName}</span></p>
          <p className="text-white/80 text-sm mt-1">Dane zostały automatycznie wypełnione. Możesz je modyfikować według potrzeb.</p>
        </div>
      )}

      {/* Wybór produktu */}
      <div>
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <span className="text-orange-500">1.</span> Wybierz produkt
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {productTypes.map((product) => (
            <CalculatorCard
              key={product.id}
              icon={product.icon}
              title={product.name}
              isActive={selectedProduct === product.id}
              onClick={() => {
                setSelectedProduct(product.id);
                setSelectedExpositorType('');
              }}
            />
          ))}
        </div>
      </div>

      {/* Wybór typu kasetonu */}
      {selectedProduct === 'kaseton' && (
        <div>
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <span className="text-orange-500">1.1</span> Wybierz rodzaj kasetonu
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => setProductParams({ ...productParams, kasetonType: 'plexi' })}
              className={`p-4 rounded-xl border-2 transition-all ${
                productParams.kasetonType === 'plexi'
                  ? 'border-orange-500 bg-orange-500/10'
                  : 'border-zinc-700 hover:border-zinc-600'
              }`}
            >
              <p className="font-medium">Kaseton z plexi</p>
              <p className="text-sm text-gray-400 mt-1">1550 zł/m²</p>
            </button>
            <button
              onClick={() => setProductParams({ ...productParams, kasetonType: 'dibond' })}
              className={`p-4 rounded-xl border-2 transition-all ${
                productParams.kasetonType === 'dibond'
                  ? 'border-orange-500 bg-orange-500/10'
                  : 'border-zinc-700 hover:border-zinc-600'
              }`}
            >
              <p className="font-medium">Kaseton z dibond</p>
              <p className="text-sm text-gray-400 mt-1">1400 zł/m²</p>
            </button>
          </div>
        </div>
      )}

      {/* Wybór typu ekspozytora */}
      {selectedProduct === 'ekspozytor' && (
        <ExpositorSection
          selectedType={selectedExpositorType}
          onTypeChange={setSelectedExpositorType}
          productParams={productParams}
          onParamsChange={setProductParams}
          selectedOptions={selectedOptions}
          onOptionsChange={setSelectedOptions}
        />
      )}

      {/* Materiał i grubość */}
      {selectedProduct && (selectedProduct !== 'ekspozytor' || selectedExpositorType) && selectedProduct !== 'kaseton' && (
        <div>
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <span className="text-orange-500">2.</span> Materiał i grubość
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {materials.map((material) => (
              <button
                key={material.id}
                onClick={() => {
                  setSelectedMaterial(material.id);
                  if (material.fixedThickness) {
                    setThickness(material.fixedThickness[0]);
                  }
                }}
                className={`p-4 rounded-xl border-2 transition-all ${
                  selectedMaterial === material.id
                    ? 'border-orange-500 bg-orange-500/10'
                    : 'border-zinc-700 hover:border-zinc-600'
                }`}
              >
                <p className="font-medium">{material.name}</p>
                <p className="text-sm text-gray-400 mt-1">
                  {material.pricePerThickness 
                    ? `${Object.values(material.pricePerThickness)[0]} zł/m²`
                    : `${material.basePrice} zł/m²`
                  }
                </p>
                {material.pricePerThickness && (
                  <p className="text-xs text-orange-400 mt-1">
                    Cena zależna od grubości
                  </p>
                )}
              </button>
            ))}
          </div>

          {selectedMaterial && (
            <div className="bg-zinc-800 rounded-xl p-6">
              <label className="block text-sm font-medium mb-3">Grubość materiału</label>
              {materials.find((m) => m.id === selectedMaterial)?.fixedThickness ? (
                <div className="flex gap-4">
                  {(materials.find((m) => m.id === selectedMaterial)?.fixedThickness || [3]).map((t) => (
                    <button
                      key={t}
                      onClick={() => setThickness(t)}
                      className={`px-6 py-3 rounded-lg font-medium transition-all ${
                        thickness === t ? 'bg-orange-500 text-white' : 'bg-zinc-700 hover:bg-zinc-600'
                      }`}
                    >
                      {t < 2 ? `${t}` : t} mm
                    </button>
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  <input
                    type="range"
                    min="1"
                    max="30"
                    step="0.5"
                    value={thickness}
                    onChange={(e) => setThickness(Number(e.target.value))}
                    className="w-full accent-orange-500"
                  />
                  <div className="flex justify-between text-sm text-gray-400">
                    <span>1 mm</span>
                    <span className="text-lg font-bold text-orange-500">{thickness} mm</span>
                    <span>30 mm</span>
                  </div>
                  <div className="grid grid-cols-6 gap-2 mt-3">
                    {[1, 1.5, 2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 30].map((t) => (
                      <button
                        key={t}
                        onClick={() => setThickness(t)}
                        className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                          thickness === t ? 'bg-orange-500 text-white' : 'bg-zinc-700 hover:bg-zinc-600'
                        }`}
                      >
                        {t}mm
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Wymiary i ilość */}
      {((selectedMaterial && selectedProduct !== 'kaseton') || (selectedProduct === 'kaseton' && productParams.kasetonType)) && (
        <div>
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <span className="text-orange-500">{selectedProduct === 'kaseton' ? '2' : '3'}.</span> Wymiary i ilość
          </h2>
          
          {/* Presety wymiarów dla formatek */}
          {selectedProduct === 'formatka' && (
            <div className="mb-4 p-4 bg-zinc-800 rounded-xl">
              <h4 className="text-sm font-medium text-gray-400 mb-3">Szybki wybór formatu</h4>
              <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                {[
                  { name: 'A6', width: 105, height: 148 },
                  { name: 'A5', width: 148, height: 210 },
                  { name: 'A4', width: 210, height: 297 },
                  { name: 'A3', width: 297, height: 420 },
                  { name: 'A2', width: 420, height: 594 },
                  { name: 'A1', width: 594, height: 841 },
                  { name: 'B2', width: 500, height: 707 },
                  { name: 'B1', width: 707, height: 1000 },
                ].map((format) => (
                  <button
                    key={format.name}
                    onClick={() => setDimensions({ ...dimensions, width: format.width, height: format.height })}
                    className="p-2 bg-zinc-700 hover:bg-orange-500 hover:text-white rounded-lg transition-all text-sm"
                  >
                    {format.name}
                    <div className="text-xs opacity-60 mt-1">
                      {format.width}×{format.height}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-zinc-800 rounded-xl p-4">
              <label className="block text-sm font-medium mb-2 text-gray-400">Szerokość</label>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => adjustDimension('width', -10)}
                  className="p-1.5 rounded-lg bg-zinc-700 hover:bg-zinc-600 transition-all flex-shrink-0"
                >
                  <Minus className="w-3 h-3" />
                </button>
                <input
                  type="number"
                  value={dimensions.width}
                  onChange={(e) => setDimensions({ ...dimensions, width: Number(e.target.value) })}
                  className="flex-1 bg-zinc-700 rounded-lg px-2 py-1.5 text-center font-mono text-sm min-w-0"
                />
                <button
                  onClick={() => adjustDimension('width', 10)}
                  className="p-1.5 rounded-lg bg-zinc-700 hover:bg-zinc-600 transition-all flex-shrink-0"
                >
                  <Plus className="w-3 h-3" />
                </button>
                <span className="text-xs text-gray-400 ml-1">mm</span>
              </div>
            </div>

            <div className="bg-zinc-800 rounded-xl p-4">
              <label className="block text-sm font-medium mb-2 text-gray-400">Wysokość</label>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => adjustDimension('height', -10)}
                  className="p-1.5 rounded-lg bg-zinc-700 hover:bg-zinc-600 transition-all flex-shrink-0"
                >
                  <Minus className="w-3 h-3" />
                </button>
                <input
                  type="number"
                  value={dimensions.height}
                  onChange={(e) => setDimensions({ ...dimensions, height: Number(e.target.value) })}
                  className="flex-1 bg-zinc-700 rounded-lg px-2 py-1.5 text-center font-mono text-sm min-w-0"
                />
                <button
                  onClick={() => adjustDimension('height', 10)}
                  className="p-1.5 rounded-lg bg-zinc-700 hover:bg-zinc-600 transition-all flex-shrink-0"
                >
                  <Plus className="w-3 h-3" />
                </button>
                <span className="text-xs text-gray-400 ml-1">mm</span>
              </div>
            </div>

            {selectedProduct !== 'formatka' && selectedProduct !== 'kaseton' && selectedProduct !== 'ledon' && (
              <div className="bg-zinc-800 rounded-xl p-4">
                <label className="block text-sm font-medium mb-2 text-gray-400">Głębokość</label>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => adjustDimension('depth', -10)}
                    className="p-1.5 rounded-lg bg-zinc-700 hover:bg-zinc-600 transition-all flex-shrink-0"
                  >
                    <Minus className="w-3 h-3" />
                  </button>
                  <input
                    type="number"
                    value={dimensions.depth}
                    onChange={(e) => setDimensions({ ...dimensions, depth: Number(e.target.value) })}
                    className="flex-1 bg-zinc-700 rounded-lg px-2 py-1.5 text-center font-mono text-sm min-w-0"
                  />
                  <button
                    onClick={() => adjustDimension('depth', 10)}
                    className="p-1.5 rounded-lg bg-zinc-700 hover:bg-zinc-600 transition-all flex-shrink-0"
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                  <span className="text-xs text-gray-400 ml-1">mm</span>
                </div>
              </div>
            )}

            <div className="bg-zinc-800 rounded-xl p-4">
              <label className="block text-sm font-medium mb-2 text-gray-400">Ilość sztuk</label>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => adjustQuantity(-1)}
                  className="p-1.5 rounded-lg bg-zinc-700 hover:bg-zinc-600 transition-all flex-shrink-0"
                >
                  <Minus className="w-3 h-3" />
                </button>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))}
                  className="flex-1 bg-zinc-700 rounded-lg px-2 py-1.5 text-center font-mono text-sm min-w-0"
                />
                <button
                  onClick={() => adjustQuantity(1)}
                  className="p-1.5 rounded-lg bg-zinc-700 hover:bg-zinc-600 transition-all flex-shrink-0"
                >
                  <Plus className="w-3 h-3" />
                </button>
                <span className="text-xs text-gray-400 ml-1">szt</span>
              </div>
            </div>
          </div>

          {/* Parametry specyficzne */}
          {selectedProduct === 'gablota' && (
            <div className="mt-4 space-y-4">
              <div className="bg-zinc-800 rounded-xl p-4">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={productParams.gabinetFrontOnly}
                    onChange={(e) => setProductParams({
                      ...productParams,
                      gabinetFrontOnly: e.target.checked
                    })}
                    className="w-4 h-4 rounded border-zinc-600 bg-zinc-700 text-orange-500 focus:ring-orange-500"
                  />
                  <div>
                    <span className="text-sm font-medium">Tylko front z plexi</span>
                    <p className="text-xs text-gray-400 mt-1">
                      Tył i boki z innego, tańszego materiału
                    </p>
                  </div>
                </label>
              </div>

              {productParams.gabinetFrontOnly && (
                <div className="bg-zinc-800 rounded-xl p-4">
                  <label className="block text-sm font-medium mb-2 text-gray-400">
                    Materiał tylnej ścianki i boków
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setProductParams({
                        ...productParams,
                        gabinetBackMaterial: 'dibond'
                      })}
                      className={`p-3 rounded-lg text-sm transition-all ${
                        productParams.gabinetBackMaterial === 'dibond'
                          ? 'bg-orange-500 text-white'
                          : 'bg-zinc-700 hover:bg-zinc-600'
                      }`}
                    >
                      <div className="font-medium">Dibond</div>
                      <div className="text-xs opacity-80 mt-1">3mm, 45 zł/m²</div>
                    </button>
                    <button
                      onClick={() => setProductParams({
                        ...productParams,
                        gabinetBackMaterial: 'sklejka'
                      })}
                      className={`p-3 rounded-lg text-sm transition-all ${
                        productParams.gabinetBackMaterial === 'sklejka'
                          ? 'bg-orange-500 text-white'
                          : 'bg-zinc-700 hover:bg-zinc-600'
                      }`}
                    >
                      <div className="font-medium">Sklejka</div>
                      <div className="text-xs opacity-80 mt-1">6mm, 30 zł/m²</div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {selectedProduct === 'kaseton' && (
            <div className="mt-4 col-span-full p-4 bg-blue-500/20 rounded-lg">
              <p className="text-sm">
                <strong>Informacja:</strong> Kaseton ma stałą głębokość konstrukcji 100mm. 
                Nie musisz jej wybierać - jest to standard dla wszystkich kasetonów.
              </p>
            </div>
          )}
          
          {selectedProduct === 'pojemnik' && (
            <>
              <div className="mt-4 bg-zinc-800 rounded-xl p-4">
                <label className="block text-sm font-medium mb-2 text-gray-400">
                  Liczba przegród wewnętrznych
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => adjustProductParam('partitions', -1)}
                    className="p-2 rounded-lg bg-zinc-700 hover:bg-zinc-600 transition-all"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <input
                    type="number"
                    value={productParams.partitions}
                    onChange={(e) =>
                      setProductParams({
                        ...productParams,
                        partitions: Math.max(0, Number(e.target.value)),
                      })
                    }
                    className="flex-1 bg-zinc-700 rounded-lg px-3 py-2 text-center font-mono max-w-[100px]"
                  />
                  <button
                    onClick={() => adjustProductParam('partitions', 1)}
                    className="p-2 rounded-lg bg-zinc-700 hover:bg-zinc-600 transition-all"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {selectedOptions.dno_inny_material && (
                <div className="mt-4 space-y-4">
                  <div className="bg-zinc-800 rounded-xl p-4">
                    <label className="block text-sm font-medium mb-2 text-gray-400">Materiał dna</label>
                    <div className="grid grid-cols-2 gap-2">
                      {materials.map((material) => (
                        <button
                          key={material.id}
                          onClick={() =>
                            setProductParams({
                              ...productParams,
                              bottomMaterial: material.id,
                            })
                          }
                          className={`p-2 rounded-lg text-sm transition-all ${
                            productParams.bottomMaterial === material.id
                              ? 'bg-orange-500 text-white'
                              : 'bg-zinc-700 hover:bg-zinc-600'
                          }`}
                        >
                          {material.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  {productParams.bottomMaterial && (
                    <div className="bg-zinc-800 rounded-xl p-4">
                      <label className="block text-sm font-medium mb-2 text-gray-400">Grubość dna</label>
                      <div className="flex gap-2">
                        {[3, 5, 8, 10].map((t) => (
                          <button
                            key={t}
                            onClick={() =>
                              setProductParams({
                                ...productParams,
                                bottomThickness: t,
                              })
                            }
                            className={`px-4 py-2 rounded-lg transition-all ${
                              productParams.bottomThickness === t
                                ? 'bg-orange-500 text-white'
                                : 'bg-zinc-700 hover:bg-zinc-600'
                            }`}
                          >
                            {t} mm
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {selectedProduct === 'ledon' && (
            <div className="mt-4 bg-zinc-800 rounded-xl p-4">
              <label className="block text-sm font-medium mb-2 text-gray-400">Długość taśmy LED (cm)</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => adjustProductParam('ledLength', -10)}
                  className="p-2 rounded-lg bg-zinc-700 hover:bg-zinc-600 transition-all"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <input
                  type="number"
                  value={productParams.ledLength}
                  onChange={(e) =>
                    setProductParams({
                      ...productParams,
                      ledLength: Math.max(10, Number(e.target.value)),
                    })
                  }
                  className="flex-1 bg-zinc-700 rounded-lg px-3 py-2 text-center font-mono max-w-[100px]"
                />
                <button
                  onClick={() => adjustProductParam('ledLength', 10)}
                  className="p-2 rounded-lg bg-zinc-700 hover:bg-zinc-600 transition-all"
                >
                  <Plus className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-400">cm</span>
              </div>
            </div>
          )}

          {selectedProduct === 'obudowa' && (
            <div className="mt-4 space-y-4">
              <div className="bg-zinc-800 rounded-xl p-4">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={productParams.includeTop}
                    onChange={(e) => setProductParams({
                      ...productParams,
                      includeTop: e.target.checked
                    })}
                    className="w-4 h-4 rounded border-zinc-600 bg-zinc-700 text-orange-500 focus:ring-orange-500"
                  />
                  <div>
                    <span className="text-sm font-medium">Dodaj górę obudowy</span>
                    <p className="text-xs text-gray-400 mt-1">
                      Domyślnie obudowa jest bez góry (otwarta)
                    </p>
                  </div>
                </label>
              </div>

              <div className="bg-zinc-800 rounded-xl p-4">
                <label className="block text-sm font-medium mb-2 text-gray-400">
                  Procent powierzchni na otwory wentylacyjne
                </label>
                <div className="space-y-2">
                  <input
                    type="range"
                    min="0"
                    max="30"
                    step="5"
                    value={productParams.ventilationPercent}
                    onChange={(e) => setProductParams({
                      ...productParams,
                      ventilationPercent: Number(e.target.value)
                    })}
                    className="w-full accent-orange-500"
                  />
                  <div className="flex justify-between text-sm text-gray-400">
                    <span>0%</span>
                    <span className="text-lg font-bold text-orange-500">{productParams.ventilationPercent}%</span>
                    <span>30%</span>
                  </div>
                </div>
              </div>

              <div className="bg-zinc-800 rounded-xl p-4">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={productParams.serviceHatch}
                    onChange={(e) => setProductParams({
                      ...productParams,
                      serviceHatch: e.target.checked
                    })}
                    className="w-4 h-4 rounded border-zinc-600 bg-zinc-700 text-orange-500 focus:ring-orange-500"
                  />
                  <div>
                    <span className="text-sm font-medium">Klapka serwisowa</span>
                    <p className="text-xs text-gray-400 mt-1">
                      200×150mm z zawiasami
                    </p>
                  </div>
                </label>
              </div>

              <div className="bg-zinc-800 rounded-xl p-4">
                <label className="block text-sm font-medium mb-2 text-gray-400">
                  Liczba przepustów kablowych
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setProductParams({
                      ...productParams,
                      cableGlands: Math.max(0, productParams.cableGlands - 1)
                    })}
                    className="p-2 rounded-lg bg-zinc-700 hover:bg-zinc-600 transition-all"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <input
                    type="number"
                    value={productParams.cableGlands}
                    onChange={(e) => setProductParams({
                      ...productParams,
                      cableGlands: Math.max(0, Number(e.target.value))
                    })}
                    className="flex-1 bg-zinc-700 rounded-lg px-3 py-2 text-center font-mono max-w-[100px]"
                  />
                  <button
                    onClick={() => setProductParams({
                      ...productParams,
                      cableGlands: productParams.cableGlands + 1
                    })}
                    className="p-2 rounded-lg bg-zinc-700 hover:bg-zinc-600 transition-all"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                {productParams.cableGlands > 0 && (
                  <p className="text-xs text-gray-500 mt-2">
                    Koszt: {productParams.cableGlands} × 15 zł = {productParams.cableGlands * 15} zł
                  </p>
                )}
              </div>
            </div>
          )}

          {(selectedProduct === 'impuls' ||
            (selectedProduct === 'ekspozytor' &&
              ['schodkowy', 'kosmetyczny'].includes(selectedExpositorType))) && (
            <div className="mt-4 bg-zinc-800 rounded-xl p-4">
              <label className="block text-sm font-medium mb-2 text-gray-400">Liczba półek</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => adjustProductParam('shelves', -1)}
                  className="p-2 rounded-lg bg-zinc-700 hover:bg-zinc-600 transition-all"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <input
                  type="number"
                  value={productParams.shelves}
                  onChange={(e) =>
                    setProductParams({
                      ...productParams,
                      shelves: Math.max(1, Math.min(5, Number(e.target.value))),
                    })
                  }
                  className="flex-1 bg-zinc-700 rounded-lg px-3 py-2 text-center font-mono max-w-[100px]"
                />
                <button
                  onClick={() => adjustProductParam('shelves', 1)}
                  className="p-2 rounded-lg bg-zinc-700 hover:bg-zinc-600 transition-all"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Opcje dodatkowe */}
      {dimensions.width > 0 && (
        <div>
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <span className="text-orange-500">{selectedProduct === 'kaseton' ? '3' : '4'}.</span> Opcje dodatkowe
          </h2>

          {selectedProduct === 'kaseton' && (
            <div className="mb-4 p-4 bg-blue-500/20 rounded-lg">
              <p className="text-sm">Wybierz typ liter dla kasetonu reklamowego</p>
            </div>
          )}

          {(selectedProduct === 'ledon' ||
            selectedOptions.led_standard ||
            selectedOptions.led_cob ||
            selectedOptions.led_rgb) && (
            <div className="mb-4 p-4 bg-yellow-500/20 rounded-lg">
              <p className="text-sm">⚡ Pamiętaj o zasilaczu LED!</p>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {getAvailableOptions().map((option) => {
              const isSelected = selectedOptions[option.id];
              const showQuantity =
                isSelected &&
                ['szt', 'mb', 'komplet'].includes(option.unit) &&
                !['polerowanie', 'led_standard', 'led_cob'].includes(option.id);

              return (
                <div key={option.id} className="relative">
                  <CalculatorCard
                    icon={option.icon}
                    title={option.name}
                    subtitle={option.price > 0 ? `${option.price} zł/${option.unit}` : option.unit === 'special' ? (option.id === 'klejenie_uv' ? '+15% do materiału' : option.id === 'wodoodpornosc' ? '+18% do LED' : option.id === 'wieko' ? 'Zwiększa zużycie materiału' : '+15-20% do LED') : option.id === 'litery_podklejane' ? 'W cenie kasetonu' : undefined}
                    isActive={isSelected}
                    onClick={() =>
                      setSelectedOptions({
                        ...selectedOptions,
                        [option.id]: !isSelected,
                      })
                    }
                  />

                  {showQuantity && (
                    <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 flex items-center gap-1 bg-zinc-800 rounded-lg px-2 py-1 border border-zinc-700">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          adjustOptionQuantity(option.id, -1);
                        }}
                        className="p-1 hover:bg-zinc-700 rounded transition-all"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="px-2 text-sm font-mono">{optionQuantities[option.id] || 1}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          adjustOptionQuantity(option.id, 1);
                        }}
                        className="p-1 hover:bg-zinc-700 rounded transition-all"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Parametry grafiki */}
          {(selectedOptions.grafika || selectedOptions.grafika_dwustronna) && (
            <div className="mt-6 bg-zinc-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-4 text-orange-400">Parametry grafiki</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2 text-gray-400">Sposób określenia wielkości grafiki</label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setProductParams({ ...productParams, graphicsSizeMode: 'percentage' })}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      productParams.graphicsSizeMode === 'percentage'
                        ? 'border-orange-500 bg-orange-500/10'
                        : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <p className="font-medium">Procent powierzchni</p>
                    <p className="text-sm text-gray-400 mt-1">Grafika pokrywa % produktu</p>
                  </button>
                  <button
                    onClick={() => setProductParams({ ...productParams, graphicsSizeMode: 'custom' })}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      productParams.graphicsSizeMode === 'custom'
                        ? 'border-orange-500 bg-orange-500/10'
                        : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <p className="font-medium">Własne wymiary</p>
                    <p className="text-sm text-gray-400 mt-1">Podaj dokładne wymiary</p>
                  </button>
                </div>
              </div>

              {productParams.graphicsSizeMode === 'percentage' && (
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-400">Procent powierzchni pokryty grafiką</label>
                  <div className="space-y-2">
                    <input
                      type="range"
                      min="10"
                      max="100"
                      step="10"
                      value={productParams.graphicsPercentage}
                      onChange={(e) => setProductParams({ ...productParams, graphicsPercentage: Number(e.target.value) })}
                      className="w-full accent-orange-500"
                    />
                    <div className="flex justify-between text-sm text-gray-400">
                      <span>10%</span>
                      <span className="text-lg font-bold text-orange-500">{productParams.graphicsPercentage}%</span>
                      <span>100%</span>
                    </div>
                  </div>
                  <div className="mt-2 p-3 bg-zinc-700 rounded-lg">
                    <p className="text-sm text-gray-300">
                      Powierzchnia grafiki: <span className="font-bold text-orange-400">
                        {(calculations.surface * (productParams.graphicsPercentage / 100)).toFixed(3)} m²
                      </span>
                    </p>
                  </div>
                </div>
              )}

              {productParams.graphicsSizeMode === 'custom' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-400">Szerokość grafiki</label>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setProductParams({ ...productParams, graphicsWidth: Math.max(10, productParams.graphicsWidth - 10) })}
                        className="p-2 rounded-lg bg-zinc-700 hover:bg-zinc-600 transition-all"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <input
                        type="number"
                        value={productParams.graphicsWidth}
                        onChange={(e) => setProductParams({ ...productParams, graphicsWidth: Math.max(10, Number(e.target.value)) })}
                        className="flex-1 bg-zinc-700 rounded-lg px-3 py-2 text-center font-mono"
                      />
                      <button
                        onClick={() => setProductParams({ ...productParams, graphicsWidth: productParams.graphicsWidth + 10 })}
                        className="p-2 rounded-lg bg-zinc-700 hover:bg-zinc-600 transition-all"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                      <span className="text-sm text-gray-400 w-10">mm</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-400">Wysokość grafiki</label>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setProductParams({ ...productParams, graphicsHeight: Math.max(10, productParams.graphicsHeight - 10) })}
                        className="p-2 rounded-lg bg-zinc-700 hover:bg-zinc-600 transition-all"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <input
                        type="number"
                        value={productParams.graphicsHeight}
                        onChange={(e) => setProductParams({ ...productParams, graphicsHeight: Math.max(10, Number(e.target.value)) })}
                        className="flex-1 bg-zinc-700 rounded-lg px-3 py-2 text-center font-mono"
                      />
                      <button
                        onClick={() => setProductParams({ ...productParams, graphicsHeight: productParams.graphicsHeight + 10 })}
                        className="p-2 rounded-lg bg-zinc-700 hover:bg-zinc-600 transition-all"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                      <span className="text-sm text-gray-400 w-10">mm</span>
                    </div>
                  </div>
                  <div className="col-span-2 mt-2 p-3 bg-zinc-700 rounded-lg">
                    <p className="text-sm text-gray-300">
                      Powierzchnia grafiki: <span className="font-bold text-orange-400">
                        {((productParams.graphicsWidth / 1000) * (productParams.graphicsHeight / 1000)).toFixed(3)} m²
                      </span>
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Kalkulacja */}
      {calculations.unitPrice > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl p-6">
              <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <span className="text-white/80">5.</span> Kalkulacja
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-white/80">Powierzchnia:</span>
                  <span className="text-xl font-mono">{calculations.surface.toFixed(3)} m²</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/80">Waga 1 szt:</span>
                  <span className="text-xl font-mono">{calculations.weight.toFixed(2)} kg</span>
                </div>

                {viewMode === 'salesperson' && (
                  <>
                    <div className="border-t border-white/20 pt-3">
                      <p className="text-sm text-white/60 mb-2">Rozbicie kosztów:</p>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-white/80">Materiał:</span>
                          <span className="font-mono">{calculations.costBreakdown.materialCost.toFixed(2)} zł</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/80">
                            Odpad ({((productTypes.find((p) => p.id === selectedProduct)?.waste || 0) * 100).toFixed(0)}%):
                          </span>
                          <span className="font-mono">{calculations.costBreakdown.wasteCost.toFixed(2)} zł</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/80">Robocizna (33.3%):</span>
                          <span className="font-mono">{calculations.costBreakdown.laborCost.toFixed(2)} zł</span>
                        </div>
                        {calculations.costBreakdown.optionsCost > 0 && (
                          <div className="flex justify-between">
                            <span className="text-white/80">Opcje dodatkowe:</span>
                            <span className="font-mono">{calculations.costBreakdown.optionsCost.toFixed(2)} zł</span>
                          </div>
                        )}
                        <div className="flex justify-between font-semibold">
                          <span className="text-white/80">Marża:</span>
                          <span className="font-mono text-green-300">{calculations.costBreakdown.margin.toFixed(2)} zł</span>
                        </div>
                        {quantity === 1 && calculations.surface < 0.1 && (
                          <div className="text-xs text-yellow-300 mt-2">
                            * Minimalne zamówienie: 0.1 m²
                          </div>
                        )}
                      </div>
                    </div>
                    {selectedProduct !== 'kaseton' && (
                      <div className="border-t border-white/20 pt-3">
                        <div className="flex justify-between items-center">
                          <span className="text-white/80">Mnożnik:</span>
                          <span className="text-lg font-mono">×{productTypes.find((p) => p.id === selectedProduct)?.multiplier || 1}</span>
                        </div>
                      </div>
                    )}
                  </>
                )}

                <div className="border-t border-white/20 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-white/80">Cena jednostkowa:</span>
                    <span className="text-2xl font-bold">{calculations.unitPrice.toFixed(2)} zł</span>
                  </div>
                </div>
                <div className="border-t border-white/20 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-white/80">Wartość całkowita:</span>
                    <span className="text-3xl font-bold">{calculations.totalPrice.toFixed(2)} zł</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-700 rounded-2xl p-6">
              <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <span className="text-white/80">6.</span> Logistyka
              </h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-white/80 text-sm">Sztuk na palecie:</span>
                    <p className="text-2xl font-mono font-bold">{calculations.directPalletArrangement.totalItems} szt</p>
                  </div>
                  <div>
                    <span className="text-white/80 text-sm">Sztuk w kartonie FEFCO:</span>
                    <p className="text-2xl font-mono font-bold">{calculations.piecesPerFefco} szt</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-white/80 text-sm">Waga jednostkowa:</span>
                    <p className="text-xl font-mono">{calculations.unitWeight.toFixed(2)} kg</p>
                  </div>
                  <div>
                    <span className="text-white/80 text-sm">Waga zbiorcza:</span>
                    <p className="text-xl font-mono">{(calculations.unitWeight * quantity).toFixed(2)} kg</p>
                  </div>
                </div>
                
                <div className="border-t border-white/20 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-white/80">Waga całkowita z opakowaniem:</span>
                    <span className="text-2xl font-bold">{calculations.totalWeight.toFixed(2)} kg</span>
                  </div>
                </div>
              </div>
              
              {/* Wizualizacja palety */}
              <PalletVisualization arrangement={calculations.directPalletArrangement} />
              
              <div className="mt-4 p-3 bg-blue-800/50 rounded-lg text-xs text-blue-200">
                <p>• Paleta EUR: 800×1200×1600 mm</p>
                <p>• Karton FEFCO: 300×400×400 mm</p>
              </div>
            </div>
          </div>

          <div className="flex justify-center mt-6">
            <Button onClick={handleAddToOffer} variant="primary" size="lg">
              <Plus className="w-5 h-5" />
              Dodaj do oferty
            </Button>
          </div>
        </>
      )}
    </div>
  );
};